import { useState } from "react";
import "./Redemption.scss";

import { Icon } from "../common/icon/Icon";
import useRedemption from "../../hooks/useRedemption";
import { renderAmount } from "../../utils/formatting";
import Redeem from "./Redeem";
import Burn from "./Burn";
import { nativeToUi } from "@uxd-protocol/uxd-client";
import { BN } from "@coral-xyz/anchor";
import Claim from "./Claim";

export enum RedemptionAction {
  Redeem = "redeem",
  Burn = "burn",
  Claim = "claim",
}

const Redemption = () => {
  const [action, setAction] = useState<RedemptionAction>(
    RedemptionAction.Redeem
  );

  const handleAction = (action: RedemptionAction) => {
    setAction(action);
  };

  const {
    loading,
    redemptionState,
    userState,
    redeemUXPForUCT,
    burnUCTForUSDC,
    calculateClaimableUSDCAmountPhaseOne,
    calculateMaxClaimableUSDCAmountPhaseTwo,
    claimIlliquidUSDC,
  } = useRedemption();
  const isPhaseTwo =
    redemptionState?.phaseOneEndTimestamp &&
    Date.now() > redemptionState?.phaseOneEndTimestamp * 1000;

  return (
    <>
      <div className="Redemption-Banner">
        UXD Protocol is sunsetting operations as of 12 October 2024{" "}
        <a
          href="https://app.realms.today/dao/UXP/proposal/9VWKEgHEMfLcf65C37HnYesK1ZoSmBW8c9K9h9xo12x5"
          target="_blank"
        >
          Click to learn more
        </a>
      </div>
      <main className="Redemption-Main">
        <div className="Redemption-Section">
          {loading ? (
            <div className="Redemption-Loading">
              <span>Loading...</span>
            </div>
          ) : (
            <div className="Redemption-Section-Schedule">
              <div className="Redemption-Section-Schedule-Phase">
                <div className="Redemption-Section-Schedule-Header">
                  <h3>Phase 1</h3>
                  <div>
                    Ends{" "}
                    {new Date(
                      (redemptionState?.phaseOneEndTimestamp ?? 0) * 1000
                    ).toDateString()}
                  </div>
                  <div>Convert UXP to UCT</div>
                </div>
                <div className="Redemption-Section-Schedule-Status">
                  <div className="Redemption-Section-Schedule-Label">
                    My Liquid USDC claimed
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {renderAmount(
                      nativeToUi(
                        new BN(userState?.phaseOneRedeemedUsdcAmount ?? 0),
                        6
                      ),
                      {
                        currency: "usdc",
                        decimals: 2,
                      }
                    )}
                  </div>
                  <div className="Redemption-Section-Schedule-Label">
                    My UXP burnt
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {renderAmount(
                      nativeToUi(new BN(userState?.burntUctAmount ?? 0), 9),
                      {
                        currency: "uxp",
                        decimals: 2,
                      }
                    )}
                  </div>
                </div>
              </div>
              <div className="Redemption-Section-Schedule-Phase">
                <div className="Redemption-Section-Schedule-Header">
                  <h3>Phase 2</h3>
                  <div>
                    {!isPhaseTwo
                      ? "Hasn't begun"
                      : redemptionState?.phaseTwoEndTimestamp
                      ? new Date(
                          redemptionState?.phaseTwoEndTimestamp * 1000
                        ).toDateString()
                      : " "}
                  </div>
                  <div>Claim USDC for UCT</div>
                </div>
                <div className="Redemption-Section-Schedule-Status">
                  <div className="Redemption-Section-Schedule-Label">
                    My Illiquid USDC claimed
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {" "}
                    {userState?.phaseTwoRedeemedUsdcAmount
                      ? renderAmount(
                          nativeToUi(
                            new BN(userState?.phaseTwoRedeemedUsdcAmount),
                            6
                          ),
                          { currency: "usdc", decimals: 2 }
                        )
                      : "0 USDC"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="Redemption-Content">
          {isPhaseTwo ? (
            <Claim
              setRedemptionAction={handleAction}
              handleClaim={claimIlliquidUSDC}
              calculateClaimableUSDCAmountPhaseTwo={
                calculateMaxClaimableUSDCAmountPhaseTwo
              }
            />
          ) : action === RedemptionAction.Redeem ? (
            <Redeem
              setRedemptionAction={handleAction}
              handleRedeem={redeemUXPForUCT}
            />
          ) : (
            <Burn
              setRedemptionAction={handleAction}
              handleBurn={burnUCTForUSDC}
              calculateClaimableUSDCAmountPhaseOne={
                calculateClaimableUSDCAmountPhaseOne
              }
            />
          )}
        </div>

        <div className="Redemption-Section">
          {loading ? (
            <div className="Redemption-Loading">
              <span>Loading...</span>
            </div>
          ) : (
            <div className="Redemption-Section-Schedule">
              <div className="Redemption-Section-Schedule-Phase">
                <div className="Redemption-Section-Schedule-Header">
                  <h3>Phase 1</h3>
                  <div>
                    Ends{" "}
                    {new Date(
                      (redemptionState?.phaseOneEndTimestamp ?? 0) * 1000
                    ).toDateString()}
                  </div>
                  <div>Convert UXP to UCT</div>
                </div>
                <div className="Redemption-Section-Schedule-Status">
                  <div className="Redemption-Section-Schedule-Label">
                    Global Liquid USDC claimed
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {renderAmount(
                      nativeToUi(
                        new BN(
                          redemptionState?.phaseOneRedeemedUsdcAmount ?? 0
                        ),
                        6
                      ),
                      {
                        currency: "usdc",
                        decimals: 2,
                      }
                    )}
                  </div>
                  <div className="Redemption-Section-Schedule-Label">
                    Global UXP burnt
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {renderAmount(
                      nativeToUi(
                        new BN(redemptionState?.totalBurntUxpAmount ?? 0),
                        9
                      ),
                      {
                        currency: "uxp",
                        decimals: 2,
                      }
                    )}
                  </div>
                </div>
              </div>
              <div className="Redemption-Section-Schedule-Phase">
                <div className="Redemption-Section-Schedule-Header">
                  <h3>Phase 2</h3>
                  <div>
                    {redemptionState?.phaseTwoEndTimestamp
                      ? new Date(
                          redemptionState?.phaseTwoEndTimestamp
                        ).toDateString()
                      : "Hasn't begun"}
                  </div>
                  <div>Claim USDC for UCT</div>
                </div>
                <div className="Redemption-Section-Schedule-Status">
                  <div className="Redemption-Section-Schedule-Label">
                    Global Illiquid USDC claimed
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {" "}
                    {redemptionState?.phaseTwoTotalRedeemableUsdcAmount
                      ? renderAmount(
                          nativeToUi(
                            new BN(
                              redemptionState?.phaseTwoTotalRedeemableUsdcAmount
                            ),
                            6
                          ),
                          { currency: "usdc", decimals: 2 }
                        )
                      : "0 USDC"}
                  </div>
                  <div className="Redemption-Section-Schedule-Label">
                    Global Total UCT
                  </div>
                  <div className="Redemption-Section-Schedule-Value">
                    {redemptionState?.redeemedUctAmount
                      ? renderAmount(
                          nativeToUi(
                            new BN(redemptionState?.redeemedUctAmount),
                            9
                          ),
                          {
                            currency: "uct",
                            decimals: 2,
                          }
                        )
                      : "0 UCT"}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="Redemption-Section-Updates">
            <div className="Redemption-Section-Updates-Title">
              Stay up to date here
            </div>
            <div className="Redemption-Section-Updates-Link">
              <a href="https://discord.gg/BEQqwVYR" target="_blank">
                {" "}
                LINK TO DISCORD{" "}
                <span>
                  <Icon name="ungroup" height={"0.7rem"} />
                </span>
              </a>
            </div>
            <div className="Redemption-Section-Updates-Link">
              <a
                href="https://app.realms.today/dao/9ybBi3CgDey94uyWPUWsXotXXivjcvZh2hzmpSfKYMNN"
                target="_blank"
              >
                REALM GOV PROPOSAL{" "}
                <span>
                  <Icon name="ungroup" height={"0.7rem"} />
                </span>
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Redemption;
